import React, { useEffect, useRef } from 'react';
import { oembed } from '@loomhq/loom-embed';

interface VideoPreviewProps {
  videoPath: string;
  videoType: string;
  videoFlag: number;
  onContinue: () => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ videoPath, videoType, videoFlag, onContinue }) => {
  const playerRef = useRef<any>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);

  const hasContinued = useRef(false);
  const safeContinue = () => {
    if (!hasContinued.current) {
      hasContinued.current = true;
      onContinue();
    }
  };

  useEffect(() => {
    if (videoType === 'youtube') {
      // Load YouTube API script if not already present
      if (!document.getElementById('youtube-iframe-api')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.id = 'youtube-iframe-api';
        document.body.appendChild(tag);
      }

      // Wait for YouTube API to be ready
      (window as any).onYouTubeIframeAPIReady = () => {
        playerRef.current = new (window as any).YT.Player('youtube-player', {
          videoId: videoPath, // already the extracted ID
          playerVars: {
            controls: videoFlag === 1 ? 0 : 1, // hides controls
            modestbranding: 1, // removes YouTube logo
            rel: 0, // don't show related videos
            fs: 0, // disables fullscreen button
            autoplay: 1, // autoplay enabled
            disablekb: 1, // disable keyboard controls
          },
          events: {
            onStateChange: (event: any) => {
              // 0 = ENDED
              if (event.data === (window as any).YT.PlayerState.ENDED) {
                console.log('🎬 YouTube video ended');
                safeContinue();
              }
            },
          },
        });
      };
    }
    if (videoType === 'vimeo') {
      const setupVimeoPlayer = () => {
        const iframe = document.getElementById('vimeo-player') as HTMLIFrameElement;
        if (iframe && (window as any).Vimeo?.Player) {
          const player = new (window as any).Vimeo.Player(iframe);
          player.on('ended', function() {
            console.log('🎬 Vimeo video ended');
            safeContinue();
          });
          playerRef.current = player;
        }
      };

      // 🔽 ADD THIS HERE
      if (!document.getElementById('vimeo-player-api')) {
        const tag = document.createElement('script');
        tag.src = 'https://player.vimeo.com/api/player.js';
        tag.id = 'vimeo-player-api';
        tag.onload = setupVimeoPlayer; // attach event only after script is loaded
        document.body.appendChild(tag);
      } else {
        setupVimeoPlayer();
      }
    }

    if (videoType === 'loom') {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== 'https://www.loom.com') return;

        let message = event.data;

        // Some browsers will send this as an object already
        if (typeof message === 'string') {
          try {
            message = JSON.parse(message);
          } catch {
            return;
          }
        }

        if (message && message.method === 'playerFinished') {
          console.log('🎬 Loom video finished');
          safeContinue();
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [videoPath, videoType, onContinue]);

  const renderVideoContent = () => {
    if (['youtube', 'vimeo', 'loom'].includes(videoType)) {
      let videosrc = '';
      if (videoType === 'youtube') {
        videosrc = `https://www.youtube.com/embed/${videoPath}?feature=oembed&enablejsapi=1`;
        return (
          <div ref={iframeContainerRef} style={{ position: 'relative', width: '100%', height: '500px' }}>
            <div id="youtube-player" style={{ width: '100%', height: '100%' }}></div>
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '1rem',
                pointerEvents: 'none',
              }}
            >
              The video call will start automatically after this intro video ends.
            </div>
          </div>
        );
      }

      if (videoType === 'vimeo') {
        videosrc =
          `https://player.vimeo.com/video/${videoPath}?title=0&byline=0&portrait=0&controls=` +
          (videoFlag === 1 ? '0' : '1') +
          `&autoplay=1`;
        return (
          <div style={{ position: 'relative', width: '100%', height: '500px' }}>
            <iframe
              id="vimeo-player"
              src={videosrc}
              width="100%"
              height="500px"
              frameBorder="0"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '1rem',
                pointerEvents: 'none',
              }}
            >
              The video call will start automatically after this intro video ends.
            </div>
          </div>
        );
      }

      if (videoType === 'loom') {
        videosrc = `https://www.loom.com/embed/${videoPath}?autoplay=true&hide_owner=true&hide_share=true&playerApi=true`;
        return (
          <div style={{ position: 'relative', width: '100%', height: '500px' }}>
            <div id="loom-player-div" style={{ width: '100%', height: '100%' }}>
              <iframe
                id="loom-player"
                src={videosrc}
                width="100%"
                height="500px"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '1rem',
                pointerEvents: 'none',
              }}
            >
              The video call will start automatically after this intro video ends.
            </div>
          </div>
        );
      }

      return (
        <iframe
          width="100%"
          height="500px"
          src={videosrc}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      );
    }

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <video
          width="100%"
          style={{ height: 'calc(100vh - 20px)' }}
          controls={videoFlag !== 1}
          autoPlay
          onEnded={onContinue}
        >
          <source src={videoPath} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '10px',
            fontSize: '1rem',
          }}
        >
          The video call will start automatically after this intro video ends.
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        background: '#000',
        height: '100vh',
        color: '#fff',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      {renderVideoContent()}
    </div>
  );
};

export default VideoPreview;
